/**
 * Metade de ENTRADA do exame eletrônico: o protocolo transcrito (E74.F1; E78.F2).
 *
 * Espelha `apps/api/types/exame_eletronico_protocolo.py`, inclusive na divisão:
 * o protocolo do CASO (`ProtocoloAssinaturaCasoData`) e o que é da PEÇA
 * (`CodigoDeclaradoInput`) desde a ADR-0077. Separado da metade de saída pelo
 * mesmo motivo do backend — o arquivo único passou do teto de 400 linhas.
 */
export type TipoAssinatura = 'qualificada_icp' | 'avancada' | 'simples' | 'nao_informado';
export type FonteCoordenada =
  | 'gps'
  | 'wifi'
  | 'rede_celular'
  | 'navegador'
  | 'ip'
  | 'nao_informada';
export type TipoEventoProtocolo =
  | 'visualizacao'
  | 'aceite'
  | 'autenticacao'
  | 'biometria'
  | 'assinatura'
  | 'liberacao';
export type PapelIp = 'cliente' | 'servidor';

export const TIPOS_ASSINATURA: readonly TipoAssinatura[] = [
  'nao_informado',
  'qualificada_icp',
  'avancada',
  'simples',
];
export const FONTES_COORDENADA: readonly FonteCoordenada[] = [
  'nao_informada',
  'gps',
  'wifi',
  'rede_celular',
  'navegador',
  'ip',
];
export const TIPOS_EVENTO: readonly TipoEventoProtocolo[] = [
  'visualizacao',
  'aceite',
  'autenticacao',
  'biometria',
  'assinatura',
  'liberacao',
];
export const PAPEIS_IP: readonly PapelIp[] = ['cliente', 'servidor'];

export interface SignatarioProtocoloData {
  nome?: string | null;
  cpf?: string | null;
  email?: string | null;
  telefone?: string | null;
}

export interface EventoProtocoloData {
  tipo: TipoEventoProtocolo;
  em_utc?: string | null;
  em_local?: string | null;
  descricao?: string | null;
}

export interface IpProtocoloData {
  endereco: string;
  papel: PapelIp;
}

export interface CoordenadaProtocoloData {
  latitude: number;
  longitude: number;
  precisao_m?: number | null;
  fonte: FonteCoordenada;
  em?: string | null;
}

export interface DispositivoProtocoloData {
  user_agent?: string | null;
  fingerprint?: string | null;
}

export interface BiometriaProtocoloData {
  presente: boolean;
  sessao_id?: string | null;
  score?: number | null;
  threshold?: number | null;
  prova_de_vida?: boolean | null;
  fornecedor?: string | null;
}

/**
 * O protocolo do PROCESSO (E78.F2, ADR-0077): um por caso, não por peça.
 *
 * O que a plataforma diz da formalização — signatário, trilha, IPs, coordenada,
 * biometria — vale para todas as peças assinadas na mesma contratação.
 */
export interface ProtocoloAssinaturaCasoData {
  plataforma?: string | null;
  codigo_verificacao?: string | null;
  envelope_id?: string | null;
  assinatura_id?: string | null;
  tipo_assinatura: TipoAssinatura;
  signatario: SignatarioProtocoloData;
  eventos: EventoProtocoloData[];
  ips: IpProtocoloData[];
  coordenada: CoordenadaProtocoloData | null;
  dispositivo: DispositivoProtocoloData | null;
  biometria: BiometriaProtocoloData;
  observacoes?: string | null;
}

/** Entrada do motor: o protocolo do caso MAIS o código declarado da peça. */
export interface ProtocoloAssinaturaData extends ProtocoloAssinaturaCasoData {
  hash_declarado?: string | null;
}

/** O que muda por peça (E78.F2): o código declarado e as observações dela. */
export interface CodigoDeclaradoInput {
  codigo_declarado?: string | null;
  observacoes?: string | null;
}

/** Separa do protocolo transcrito o que é do CASO — o resto é da peça. */
export function protocoloDoCaso(p: ProtocoloAssinaturaData): ProtocoloAssinaturaCasoData {
  const { hash_declarado: doPeca, ...doCaso } = p;
  void doPeca;
  return doCaso;
}
